import React, { useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { Monitor, Send, Settings } from 'lucide-react';
import { APP_CONFIG } from '../../config/app';
import { useFounderPhoto } from '../../hooks/useFounderPhoto';
import TrackedContactLink from '../TrackedContactLink';
import { HERO_CONVERSATIONS } from './data';
import type { ChatMessage } from './types';

type Sender = ChatMessage['sender'];

/** Chronologie d'une conversation (ms) : indicateur « … » puis frappe de chaque message. */
const TIMELINE: ReadonlyArray<{ at: number; indicator?: Sender; typeIndex?: number }> = [
  { at: 300, indicator: 'user' },
  { at: 1500, typeIndex: 0 },
  { at: 3700, indicator: 'shop' },
  { at: 4900, typeIndex: 1 },
  { at: 7200, indicator: 'user' },
  { at: 8400, typeIndex: 2 },
];
/** Passage à la conversation suivante (pause d'environ 4 s après le dernier message). */
const NEXT_CONVERSATION_AT = 13500;
const TYPING_SPEED_MS = 20;

const bubbleClass = (sender: Sender) =>
  sender === 'user' ? 'bg-surface-2 rounded-2xl rounded-tl-md' : 'bg-accent-strong rounded-2xl rounded-tr-md';

interface ChatMockupProps {
  /** Élément observé pour le léger parallaxe du téléphone. */
  scrollTarget: React.RefObject<HTMLElement | null>;
}

/** Mockup iPhone qui fait tourner une conversation WhatsApp par activité. */
const ChatMockup: React.FC<ChatMockupProps> = ({ scrollTarget }) => {
  const prefersReducedMotion = useReducedMotion() === true;
  const [conversationIndex, setConversationIndex] = useState(0);
  // Photo du fondateur en avatar (même source que « Qui suis-je ») ; icône de la conversation à défaut.
  const { loading: avatarLoading, photoUrl: avatarUrl, handlePhotoError: handleAvatarError } = useFounderPhoto();
  const [visibleCount, setVisibleCount] = useState(0);
  const [typingIndex, setTypingIndex] = useState(-1);
  const [typingText, setTypingText] = useState('');
  const [indicator, setIndicator] = useState<Sender | null>(null);
  const timersRef = useRef<Array<ReturnType<typeof setTimeout>>>([]);

  const { scrollYProgress } = useScroll({ target: scrollTarget, offset: ['start start', 'end start'] });
  const phoneParallaxY = useTransform(scrollYProgress, [0, 1], [0, prefersReducedMotion ? 0 : 32]);

  const conversation = HERO_CONVERSATIONS[conversationIndex];
  const { messages } = conversation;
  const HeaderIcon = conversation.icon;

  // Planifie la conversation courante puis passe à la suivante
  useEffect(() => {
    if (prefersReducedMotion) return;

    const timers = TIMELINE.map((step) =>
      setTimeout(() => {
        if (step.indicator) {
          setIndicator(step.indicator);
        } else if (step.typeIndex !== undefined) {
          setIndicator(null);
          setTypingText('');
          setTypingIndex(step.typeIndex);
        }
      }, step.at),
    );
    timers.push(
      setTimeout(() => {
        setVisibleCount(0);
        setTypingIndex(-1);
        setTypingText('');
        setIndicator(null);
        setConversationIndex((i) => (i + 1) % HERO_CONVERSATIONS.length);
      }, NEXT_CONVERSATION_AT),
    );
    timersRef.current = timers;

    return () => timersRef.current.forEach(clearTimeout);
  }, [conversationIndex, prefersReducedMotion]);

  // Effet de frappe caractère par caractère
  useEffect(() => {
    if (typingIndex < 0) return;
    const fullText = messages[typingIndex].text;
    const timer = setTimeout(() => {
      if (typingText.length < fullText.length) {
        setTypingText(fullText.substring(0, typingText.length + 1));
      } else {
        setTypingIndex(-1);
        setVisibleCount(typingIndex + 1);
      }
    }, TYPING_SPEED_MS);
    return () => clearTimeout(timer);
  }, [typingText, typingIndex, messages]);

  const shownCount = prefersReducedMotion ? messages.length : visibleCount;

  return (
    <TrackedContactLink
      channel="whatsapp"
      service="autre"
      ctaLocation="hero"
      href={APP_CONFIG.contact.whatsappUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="block cursor-pointer rounded-[55px] transition-shadow duration-200 hover:ring-1 hover:ring-line-strong"
      aria-label="Écrire à SOS IT Services sur WhatsApp"
    >
      <motion.div style={{ y: phoneParallaxY }} className="relative w-[300px] xl:w-[320px] animate-hero-iphone">
        <div className="relative h-[610px] xl:h-[650px] overflow-hidden rounded-[55px] border-[3px] border-line-strong bg-black shadow-[0_40px_80px_-40px_color-mix(in_srgb,var(--color-accent)_35%,transparent)]">
          {/* Dynamic Island */}
          <div className="absolute left-1/2 top-2 z-20 flex h-[32px] w-[110px] -translate-x-1/2 items-center justify-center rounded-full bg-black" aria-hidden>
            <div className="absolute right-6 flex h-[11px] w-[11px] items-center justify-center rounded-full bg-surface-2">
              <div className="h-[6px] w-[6px] rounded-full bg-line-strong" />
            </div>
          </div>

          <div className="relative h-full rounded-[52px] bg-canvas p-4">
            {/* En-tête façon WhatsApp */}
            <div className="mb-4 mt-14 flex items-center justify-between rounded-xl border border-line bg-surface px-3 py-2">
              <div className="flex items-center space-x-3">
                <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-accent-strong">
                  {!avatarLoading && avatarUrl ? (
                    <img
                      key={avatarUrl}
                      src={avatarUrl}
                      alt=""
                      width={40}
                      height={40}
                      className="h-full w-full object-cover"
                      onError={handleAvatarError}
                    />
                  ) : (
                    <HeaderIcon className="h-5 w-5 text-white" aria-hidden />
                  )}
                </div>
                <div>
                  <p className="text-sm font-semibold text-fg">SOS IT Services</p>
                  <p className="text-xs text-fg-muted">en ligne</p>
                </div>
              </div>
              <div className="flex items-center space-x-4" aria-hidden>
                <Monitor className="h-5 w-5 text-fg-subtle" />
                <Settings className="h-5 w-5 text-fg-subtle" />
              </div>
            </div>

            {/* Messages */}
            <div className="min-h-[180px] space-y-4 pb-24" aria-hidden>
              {messages.map((message, index) => {
                const isTypingThis = typingIndex === index;
                if (index >= shownCount && !isTypingThis) return null;
                const isUser = message.sender === 'user';
                return (
                  <div key={`${conversation.id}-${index}`} className={`flex ${isUser ? '' : 'justify-end'}`}>
                    <div className={`${bubbleClass(message.sender)} relative max-w-[80%] p-3`}>
                      <p className={`${isUser ? 'text-fg-muted' : 'text-white'} text-sm`}>
                        {isTypingThis ? typingText : message.text}
                        {isTypingThis && <span className="ml-1 inline-block h-3 w-1 animate-pulse bg-fg-muted" />}
                      </p>
                      <p className={`${isUser ? 'text-fg-muted' : 'text-white/80'} mt-1 text-[11px] tabular-nums`}>
                        {message.time} {isUser ? '✓' : '✓✓'}
                      </p>
                    </div>
                  </div>
                );
              })}
              {indicator && (
                <div className={`flex ${indicator === 'user' ? '' : 'justify-end'}`}>
                  <div className={`${bubbleClass(indicator)} min-w-[40px] p-3`}>
                    <span className={`flex items-center gap-[3px] ${indicator === 'user' ? 'text-fg-muted' : 'text-fg'}`}>
                      {[0, 300, 600].map((delay) => (
                        <span
                          key={delay}
                          className="inline-block h-1 w-1 animate-bounce rounded-full bg-current opacity-60"
                          style={{ animationDelay: `${delay}ms` }}
                        />
                      ))}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Zone de saisie (décorative) */}
            <div className="absolute bottom-8 left-4 right-4 flex items-center gap-2" aria-hidden>
              <div className="flex flex-grow items-center rounded-full border border-line bg-surface p-3 px-4 text-sm text-fg-subtle">
                Message
              </div>
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-accent-strong">
                <Send className="h-5 w-5 text-white" />
              </span>
            </div>

            <div className="absolute bottom-1 left-1/2 -translate-x-1/2" aria-hidden>
              <div className="h-1 w-[120px] rounded-full bg-line-strong" />
            </div>
          </div>

          {/* Boutons latéraux */}
          <div className="absolute -right-[3px] top-[90px] h-[38px] w-[3px] rounded-l bg-line-strong" aria-hidden />
          <div className="absolute -right-[3px] top-[150px] h-[65px] w-[3px] rounded-l bg-line-strong" aria-hidden />
          <div className="absolute -left-[3px] top-[90px] h-[32px] w-[3px] rounded-r bg-line-strong" aria-hidden />
        </div>
      </motion.div>
    </TrackedContactLink>
  );
};

export default ChatMockup;
